import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";

import {
  attachTag,
  countTagsOf,
  createTag,
  detachTagFrom,
  fetchTags,
  normalizeTagName,
  TAG_NAME_MAX,
} from "../lib/media/tags";
import { queryKeys } from "../lib/queryKeys";
import { useToast } from "../lib/toast";
import QueryState from "./QueryState";
import SheetShell from "./SheetShell";

interface Props {
  /** 付け外しの対象。詳細表示からは1件、一覧の一括操作からは選んだ全件を渡す */
  mediaIds: string[];
  onClose: () => void;
}

/**
 * 写真・動画へのタグの付与・解除と新規登録の面。
 * スマホは下からのシート、PC は中央カード(SheetShell に任せる)。
 * 対象が複数のときは、タグごとに塗り(全件)・縁取り(一部)・素(未付与)の三段で表す
 * (1件のときは on/off の二段と同じ見た目になる)。
 */
export default function TagPicker({ mediaIds, onClose }: Props) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState("");
  const count = mediaIds.length;

  const tags = useQuery({ queryKey: queryKeys.tags, queryFn: fetchTags });
  const counts = useQuery({
    queryKey: queryKeys.mediaTagCounts(mediaIds),
    queryFn: () => countTagsOf(mediaIds),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.mediaTagCounts(mediaIds) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.tags });
    // タグで絞り込んでいる一覧に増減が映るように取り直す
    await queryClient.invalidateQueries({ queryKey: queryKeys.timelines });
  };

  const toggle = useMutation({
    mutationFn: async ({ tagId, on }: { tagId: string; name: string; on: boolean }) => {
      if (on) await attachTag(mediaIds, tagId);
      else await detachTagFrom(mediaIds, tagId);
    },
    onSuccess: async (_, { name, on }) => {
      await refresh();
      toast(
        count === 1
          ? on
            ? `タグ「${name}」を付与しました。`
            : `タグ「${name}」を解除しました。`
          : on
            ? `${String(count)}件にタグ「${name}」を付与しました。`
            : `${String(count)}件からタグ「${name}」を解除しました。`,
      );
    },
    onError: (e, { name, on }) => {
      // 詳細はユーザーに出さない。原因はコンソールで追う
      console.error("タグの付け外しに失敗", e);
      toast(
        on ? `タグ「${name}」の付与に失敗しました。` : `タグ「${name}」の解除に失敗しました。`,
        "error",
      );
    },
  });

  const register = useMutation({
    mutationFn: async (name: string) => {
      const tag = await createTag(name);
      await attachTag(mediaIds, tag.id);
    },
    onSuccess: async (_, name) => {
      setDraft("");
      await refresh();
      toast(`タグ「${name}」を登録しました。`);
    },
    onError: (e, name) => {
      console.error("タグの登録に失敗", e);
      toast(`タグ「${name}」の登録に失敗しました。`, "error");
    },
  });

  function submit() {
    const name = normalizeTagName(draft);
    if (name === null) return;
    // createTag は同名なら既存を返して「成功」してしまう。登録済みはここで知らせる
    if (tags.data?.some((tag) => tag.name === name) ?? false) {
      toast(`タグ「${name}」は登録済みです。`, "error");
      return;
    }
    register.mutate(name);
  }

  // 付け外しの最中は全部のチップを止める。一括は1回が重く、連打すると
  // 同じ処理が重なって走る。動いているチップだけ薄くして、どれが処理中かを見せる
  const busy = toggle.isPending || register.isPending;
  const workingTagId = toggle.isPending ? toggle.variables.tagId : null;

  return (
    <SheetShell
      title={count === 1 ? "タグ" : `${String(count)}件にタグを付与`}
      onClose={onClose}
      center
    >
      <form
        className="flex shrink-0 gap-2 border-b border-[var(--color-line)] p-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          maxLength={TAG_NAME_MAX}
          placeholder="新しいタグ名"
          autoComplete="off"
          data-1p-ignore
          className="min-w-0 flex-1 rounded-lg border border-[var(--color-line)] bg-white px-3 py-2 text-base"
        />
        <button
          type="submit"
          disabled={normalizeTagName(draft) === null || register.isPending}
          className="flex shrink-0 items-center gap-1 rounded-lg bg-[var(--color-accent)] px-3 py-2 text-sm text-white transition enabled:hover:opacity-85 disabled:opacity-50"
        >
          <Plus className="size-4" aria-hidden />
          登録
        </button>
      </form>

      {/* タグが増えても入力欄は見えたままにする(一覧だけが流れる) */}
      <div className="flex min-h-32 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain p-4">
        <QueryState
          isPending={tags.isPending || counts.isPending}
          isError={tags.isError || counts.isError}
          errorMessage="タグの取得に失敗しました。"
          isEmpty={tags.isSuccess && counts.isSuccess && tags.data.length === 0}
          emptyMessage="タグは登録されていません。"
        />
        {tags.isSuccess && counts.isSuccess && tags.data.length > 0 && (
          <>
            <div className="flex flex-wrap gap-2">
              {tags.data.map((tag) => {
                const attached = counts.data.get(tag.id) ?? 0;
                const all = attached === count;
                const some = attached > 0 && !all;
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => {
                      // 全件に付いているものだけが解除。一部のものは残りに揃える
                      toggle.mutate({ tagId: tag.id, name: tag.name, on: !all });
                    }}
                    disabled={busy}
                    aria-pressed={all}
                    aria-label={some ? `${tag.name}(${String(attached)}件に付与済み)` : undefined}
                    className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                      workingTagId === tag.id ? "opacity-60" : ""
                    } ${
                      all
                        ? "bg-[var(--color-accent)] text-white enabled:hover:opacity-85"
                        : some
                          ? "border-[1.5px] border-[var(--color-accent)] font-medium text-[var(--color-accent)] enabled:hover:bg-[color-mix(in_srgb,var(--color-accent)_8%,transparent)]"
                          : "border border-[var(--color-line)] text-[var(--color-ink)] enabled:hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
                    }`}
                  >
                    {tag.name}
                    {some && (
                      <span className="ml-1 text-xs tabular-nums">{`${String(attached)}/${String(count)}`}</span>
                    )}
                  </button>
                );
              })}
            </div>
            {/* 三段の意味は見ただけでは分からないので、一括のときだけ下に一行添える(1件なら on/off しかない) */}
            {count > 1 && (
              <p className="text-xs text-[var(--color-sub)]">
                塗りは全件、縁取りは一部に付与済みです。押すと全件に付与・解除します。
              </p>
            )}
          </>
        )}
      </div>
    </SheetShell>
  );
}
